/**
 * Help that teaches (v7 Part D §8 patterns 19–20, §10.15): one sentence of help per card, kept in one registry; the
 * keyboard shortcuts on "?"; a three-step first visit per workspace.
 */
import { Fragment, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Dialog } from './overlays';
import { Button, cx } from './primitives';

const SHORTCUTS: [string, string][] = [
  ['Ctrl + K', 'Find a customer, an order, a route, an action or a screen'],
  ['/', 'Search this list'],
  ['J / K', 'Next / previous row; with a row open, open the next one'],
  ['Enter', 'Open the row'],
  ['Esc', 'Close'],
  ['?', 'These shortcuts'],
];

/** "?" anywhere (outside a text field) shows the shortcuts; "/" focuses the page's list search. */
export function ShortcutsHelp() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === '?') { e.preventDefault(); setOpen(true); }
      if (e.key === '/') {
        const search = document.querySelector<HTMLInputElement>('main input[type="search"], main input[placeholder*="Search" i], main input[aria-label*="Search" i]');
        if (search) { e.preventDefault(); search.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <Dialog open={open} onClose={() => setOpen(false)} title="Keyboard shortcuts">
      <dl className="grid grid-cols-[7rem_1fr] gap-x-4 gap-y-2 text-body">
        {SHORTCUTS.map(([k, v]) => (<Fragment key={k}><dt className="font-mono text-small text-ink">{k}</dt><dd className="text-ink-muted">{v}</dd></Fragment>))}
      </dl>
    </Dialog>
  );
}

/** Three steps for a person's first visit to a workspace (pattern 20). */
export const TOURS: Record<string, [string, string][]> = {
  home: [['My day', 'Tomorrow in one sentence, what needs you, and how the day\'s automatic steps went.'], ['Inbox', 'Everything that needs a person, most urgent first.'], ['Ctrl + K', 'Find any customer by name, code, the last 4 digits of the phone, or the door QR.']],
  crm: [['Customer 360', 'Every customer; click one to see them, J and K to move to the next.'], ['Exception center', 'Skips, pauses and quantity changes by date.'], ['Reports & sheets', 'Download any list as Excel.']],
  operations: [['Tomorrow', 'Tomorrow prepares itself at the cut-off; this board shows how it went.'], ['Live delivery', 'The rounds out now, door by door.'], ['Routes & riders', 'Each round\'s route, its usual rider and its stop order.']],
  supply: [['Overview', 'The day\'s milk from the farms to the doors on one page.'], ['Farm overview', 'Each farm\'s day: collected, tested, sent.'], ['Plant overview', 'What came in, what was filled, what went out.']],
  money: [['Rider statements', 'What each rider earned, line by line.'], ['Farmer payments', 'Each locus\'s settlement for the period.'], ['Reports', 'Pay and settlement reports for any period.']],
  people: [['People', 'Everyone who works here, and who signs in to the phones.'], ['Attendance & shifts', 'Who was present, and their shifts.'], ['Reports', 'Attendance for any period.']],
  insights: [['Director dashboard', 'The business for a period against the one before.'], ['Every figure', 'Click a figure to see the rows that make it.'], ['All reports', 'Every report of every workspace.']],
  admin: [['Control Centre', 'Every time of the day, every rule and every job, in one place.'], ['Second person', 'Important changes wait for another person\'s approval.'], ['Roadmap', 'What is coming, and in which release.']],
};

export function WorkspaceTour({ workspace, seen, onDone }: { workspace: string; seen: boolean; onDone: () => void }) {
  const [step, setStep] = useState(0);
  const steps = TOURS[workspace];
  if (seen || !steps) return null;
  const [title, body] = steps[step]!;
  return (
    <div role="dialog" aria-label="A short tour" className="fixed bottom-6 right-6 z-50 w-80 rounded-[var(--radius-panel)] border border-line bg-surface p-4 shadow-[var(--shadow-overlay)]">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-small text-ink-muted">{step + 1} of {steps.length}</span>
        <button type="button" aria-label="Close the tour" onClick={onDone} className="text-ink-faint hover:text-ink"><X size={14} /></button>
      </div>
      <div className="font-medium text-ink">{title}</div>
      <p className="mt-1 text-small text-ink-muted">{body}</p>
      <div className="mt-3 flex justify-end gap-2">
        {step > 0 ? <Button size="dense" onClick={() => setStep(step - 1)}>Back</Button> : null}
        <Button size="dense" variant="primary" className={cx()} onClick={() => (step + 1 < steps.length ? setStep(step + 1) : onDone())}>{step + 1 < steps.length ? 'Next' : 'Done'}</Button>
      </div>
    </div>
  );
}
